import type {RuntimeEnvironment} from "@common-core/web-component-runtime/runtime";
import {consume, provide} from "@lit/context";
import {Task} from "@lit/task";
import {LitElement, css, html} from "lit";
import {customElement} from "lit/decorators.js";
import {unsafeStatic} from "lit/static-html.js";

import type {BridgeBarConfig} from "../config";
import {BridgeBarConfigContext} from "../config";
import {ROOT_TAG} from "../globals";
import type {Environment} from "../runtime";
import {RuntimeContext} from "../runtime";
import {buildMembershipApiUrl} from "./buildMembershipApiUrl";
import {MembershipDataContext} from "./MembershipDataContext";
import {toPartitionQueryParameter} from "./toPartitionQueryParameter";
import type {MembershipDataContextValue} from "./MembershipData";
import {isCommonOrgEntities} from "./MembershipData";

const tagName = `${ROOT_TAG}-membership-data-provider`;
export const MembershipDataProvider = unsafeStatic(tagName);

/**
 * MembershipDataProviderElement fetches the current user's Common Org entities
 * (the orgs they are a member of) from the Bridge Bar API and provides the
 * result via MembershipDataContext to descendant consumers (the Org Context
 * Selector).
 *
 * On fetch failure it provides an error status through context rather than
 * throwing — a thrown error in a provider would crash the host bar.
 * The provider always renders `<slot></slot>` regardless of fetch status.
 */
@customElement(tagName)
export class MembershipDataProviderElement extends LitElement {
  /*
   * A context provider with no visual output of its own, but it sits in the
   * bar's flex row between the container and the selector. Left as the default
   * inline host it has no definite width, so percentage max-widths inside the
   * selector cannot resolve and the trigger can never be told to shrink.
   * `display: contents` removes it from the layout entirely so the selector
   * becomes a direct flex item of the container's slot wrapper.
   */
  static styles = css`
    :host {
      display: contents;
    }
  `;

  @consume({context: RuntimeContext, subscribe: true})
  runtimeEnvironment: RuntimeEnvironment<Environment, unknown>;

  @consume({context: BridgeBarConfigContext, subscribe: true})
  private bridgeBarConfig: BridgeBarConfig | undefined;

  @provide({context: MembershipDataContext})
  membershipData: MembershipDataContextValue = {status: "pending"};

  private readonly fetchMembershipData = new Task<
    readonly [
      RuntimeEnvironment<Environment, unknown> | undefined,
      BridgeBarConfig | undefined
    ]
  >(this, {
    args: () => [this.runtimeEnvironment, this.bridgeBarConfig] as const,

    task: async ([runtimeEnvironment, config]) => {
      if (!runtimeEnvironment?.environment?.bridgeBarApiUrl) {
        // Runtime not yet initialized — stay pending
        return;
      }

      // The adopting app must opt in (context-aware). Until it does, do not
      // fetch and stay pending so the selector renders nothing.
      if (config?.contextType !== "common-org-entity") {
        return;
      }

      const {environment, runtime} = runtimeEnvironment;
      const partitionQueryParameter = toPartitionQueryParameter(
        runtime.partition
      );

      const url = buildMembershipApiUrl(
        environment.bridgeBarApiUrl,
        partitionQueryParameter,
        config
      );
      const correlationId = crypto.randomUUID();

      let response: Response;
      try {
        response = await fetch(url, {
          credentials: "include",
          mode: "cors",
          headers: {
            "Accept": "application/vnd.coxauto.v1+json",
            "X-CoxAuto-Correlation-Id": correlationId
          }
        });
      } catch (networkError) {
        this.membershipData = {
          status: "error",
          error:
            networkError instanceof Error
              ? networkError
              : new Error(String(networkError))
        };
        return;
      }

      if (!response.ok) {
        this.membershipData = {
          status: "error",
          error: new Error(
            `Failed to fetch memberships: ${response.status} ${response.statusText}`
          )
        };
        return;
      }

      let parsed: unknown;
      try {
        parsed = await response.json();
      } catch (parseError) {
        this.membershipData = {
          status: "error",
          error:
            parseError instanceof Error
              ? parseError
              : new Error(String(parseError))
        };
        return;
      }

      if (!isCommonOrgEntities(parsed)) {
        this.membershipData = {
          status: "error",
          error: new Error(
            "Malformed membership response: expected { entities: CommonOrgEntity[], " +
              "showSearch?: boolean, defaultEntity?: CommonOrgEntity }"
          )
        };
        return;
      }

      this.membershipData = {status: "complete", data: parsed};
    }
  });

  render() {
    return html`<slot></slot>`;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    [tagName]: MembershipDataProviderElement;
  }
}
